// =========================================================================
// TYPES & SCHEMAS
// =========================================================================

export interface Produto {
  id: string
  nome: string
  codigo_barras: string
  tipo_controle: 'simples' | 'serializado' | 'patrimonio'
  grupo: string           // Ex: 'Madeira', 'Tecido', 'Ferragens'
  subgrupo: string        // Ex: 'Chapa', 'Suede', 'Parafuso'
  unidade_medida_padrao: string
  estoque_minimo: number
  agrupador_comercial: string
  preco_compra?: number
  preco_venda?: number
  peso_medio_unidade?: number
  observacoes?: string    // campo livre para especificações
}

export interface Lote {
  id: string
  codigo_lote: string
  produto_id: string
  tom_especifico: string
  tipo_madeira?: string
  quantidade_nota: number // Nota total na unidade base (Ex: m3 para madeira, ml para tecidos, kg para malha/cola)
  custo_unitario_compra: number
  data_recebimento: string
  peso_medio_recalculado?: number // Média ponderada kg/un ou kg/galão dinâmica
  fornecedor_id?: string // Vinculado a Entidade
  numero_nota?: string
  nota_fiscal_photo?: string
  tipo_controle?: 'simples' | 'serializado' | 'patrimonio'
  preco_venda_calculado?: number
  data_nota_fiscal?: string

  // Dynamic Inbound variation fields
  tamanho?: string      // Para parafusos e dimensões (Ex: '5x30')
  cor?: string          // Acabamento (Ex: 'Bicromatizado')
  tipo_tear?: string    // Para malhas (Ex: 'Circular')
  micras?: number       // Para malhas (Ex: 150)
  gramatura?: number    // Para TNT (Ex: 60)
  largura?: number      // Largura de rolos (tecido/TNT)
  comprimento?: number  // Comprimento específico (madeira)
  espessura?: number    // Espessura específica (madeira)
  umidade?: number      // Teor de umidade % (madeira)
  tipo_cabeca?: string  // Tipo de cabeça (Ex: 'Sextavado', 'Philips')
  pressao?: string      // Pressão do pistão (Ex: '600N')
  lado_conjunto?: string // Lado da articulação (Ex: 'Par')
  peso_rolo?: number    // Peso real do rolo (tecido)
  marca?: string        // Marca do insumo
  condicao?: string // Condição do lote
}

export interface Entidade {
  id: string
  tipo_pessoa: 'PF' | 'PJ'
  tipo_relacao: 'cliente' | 'fornecedor' | 'funcionario' | 'transportadora' | 'representante'
  nome: string
  nome_fantasia?: string
  documento: string
  inscricao_estadual?: string
  telefone: string
  email: string
  endereco?: string
  cidade?: string
  uf?: string
  cep?: string
  observacao: string
  ativo: boolean
}

export interface LocalArmazenamento {
  id: string
  codigo_local: string
  tipo: 'gaiola' | 'corredor' | 'prateleira' | 'patio' | 'geral'
  localizacao: string
  capacidade_maxima?: number
  unidade_capacidade?: 'm3' | 'ml' | 'rolo' | 'un' | 'kg'
}

export interface Saldo {
  produto_id: string
  lote_id: string
  local_id: string
  quantidade_fisica: number
  quantidade_reservada: number
}

export interface Movimentacao {
  id: string
  data_movimento: string
  produto_id: string
  lote_id: string
  local_origem_id?: string
  local_destino_id?: string
  tipo_movimento: 'entrada' | 'saida' | 'transferencia' | 'ajuste'
  motivo: 'compra' | 'producao' | 'venda' | 'balanco' | 'perda' | 'emprestimo' | 'devolucao'
  quantidade: number
  usuario: string
}

export interface Reserva {
  id: string
  numero_orcamento: string
  produto_id: string
  lote_id: string
  quantidade_reservada: number
  status: 'reservado' | 'vendido' | 'expirado' | 'rascunho'
  validade: string
  cliente_id?: string // Vinculado a Entidade
}

export interface ItemOperacional {
  id: string
  nome: string
  codigo_patrimonio?: string
  codigo_barras?: string
  categoria: string
  quantidade_total: number
  quantidade_disponivel: number
  estoque_minimo: number
  localizacao_sala: string
  status: string
  custo_aquisicao?: number
  observacoes_modelo?: string // Especificações técnicas para compra de modelo idêntico
  marca?: string
  agrupador_comercial?: string
  tipo_un?: string
  fornecedor_nome?: string
}

export interface Vale {
  id: string
  item_operacional_id: string
  funcionario_nome: string
  cpf_cnpj: string
  setor: string
  data_emprestimo: string
  data_devolucao?: string
  valor_cobranca: number
  tipo: string
  status: string
  observacoes?: string
}

export interface ImportRow {
  id: string
  produto_id: string
  codigo_lote: string
  tom_especifico: string
  tipo_madeira?: string
  quantidade: number // quantidade base
  unidade: string
  fornecedor_id?: string // Vinculado a Entidade (Fornecedor)
  local_destino_id?: string
  // Control attributes for grid conversions
  ml?: number
  m2?: number
  rolos?: number
  peso?: number
  unidades?: number
  pallets?: number
  volume?: number
  pecas?: number
  auto_split?: boolean

  // Dynamic Inbound variation fields
  tamanho?: string      // Para parafusos e dimensões (Ex: '5x30')
  cor?: string          // Acabamento (Ex: 'Bicromatizado')
  tipo_tear?: string    // Para malhas (Ex: 'Circular')
  micras?: number       // Para malhas (Ex: 150)
  gramatura?: number    // Para TNT (Ex: 60)
  largura?: number      // Largura de rolos (tecido/TNT)
  comprimento?: number  // Comprimento específico (madeira)
  espessura?: number    // Espessura específica (madeira)
  umidade?: number      // Teor de umidade % (madeira)
  tipo_cabeca?: string  // Tipo de cabeça (Ex: 'Sextavado', 'Philips')
  pressao?: string      // Pressão do pistão (Ex: '600N')
  lado_conjunto?: string // Lado da articulação (Ex: 'Par')
  peso_rolo?: number    // Peso real do rolo (tecido)
  marca?: string        // Marca do insumo
  condicao?: string // Condição do lote
}

export interface CategoriaGrupo {
  id: string
  nome: string
  subgrupos: string[]
}

export interface SystemParameters {
  grupos: CategoriaGrupo[]
  unidadesMedida: string[]
  categoriasAlmoxarifado: string[]
  setoresFabrica: string[]
  representacoesComerciais: string[]
}

export const INITIAL_PARAMETERS: SystemParameters = {
  grupos: [
    { id: 'grp-madeira', nome: 'MADEIRA', subgrupos: ['CHAPA', 'SERRADA', 'COMPENSADO', 'MDP', 'MDF'] },
    { id: 'grp-tecido', nome: 'TECIDO', subgrupos: ['SUEDE', 'LINHO', 'LINHÃO', 'CORINO', 'VELUDO', 'CHENILLE'] },
    { id: 'grp-tnt', nome: 'TNT', subgrupos: ['TNT LISO', 'TNT GRAMATURA ALTA'] },
    { id: 'grp-malha', nome: 'MALHA', subgrupos: ['MALHA REVESTIMENTO', 'MALHA PIQUET'] },
    { id: 'grp-cola', nome: 'COLA', subgrupos: ['COLA BRANCA', 'COLA QUENTE', 'ADESIVO SPRAY'] },
    { id: 'grp-ferragens', nome: 'FERRAGENS', subgrupos: ['GRAMPO', 'PARAFUSO', 'DOBRADIÇA', 'PUXADOR', 'PÉ', 'ARTICULAÇÃO', 'PISTÃO', 'BUCHA', 'CANTONEIRA', 'ARCO'] },
    { id: 'grp-acabamento', nome: 'ACABAMENTO', subgrupos: ['LIXA', 'VERNIZ', 'TINTA', 'SELADOR'] },
    { id: 'grp-embalagem', nome: 'EMBALAGEM', subgrupos: ['PAPELÃO', 'PLÁSTICA', 'FITA ADESIVA'] },
    { id: 'grp-ferramenta', nome: 'FERRAMENTA', subgrupos: ['PNEUMÁTICA', 'ELÉTRICA', 'MANUAL', 'BROCA', 'DISCO'] },
    { id: 'grp-operacional', nome: 'OPERACIONAL', subgrupos: ['CABO', 'LUBRIFICANTE', 'EPI', 'PEÇA REPOSIÇÃO'] }
  ],
  unidadesMedida: ['UN', 'ML', 'M2', 'M3', 'KG', 'CX', 'PCT', 'PAR', 'ROLO', 'L'],
  categoriasAlmoxarifado: ['FERRAMENTA', 'CONSUMIVEL', 'OPERACIONAL'],
  setoresFabrica: ['MARCENARIA', 'TAPEÇARIA', 'COSTURA', 'MONTAGEM', 'EMBALAGEM', 'EXPEDIÇÃO', 'ADMINISTRATIVO'],
  representacoesComerciais: ['BEGE', 'PRETO', 'BRANCO', 'CINZA', 'MARROM', 'GRAFITE', 'CASAL', 'SOLTEIRO']
}

// =========================================================================
// INITIAL SEED DATA
// =========================================================================

export const INITIAL_PRODUTOS: Produto[] = []

export const INITIAL_ENTIDADES: Entidade[] = []

export const INITIAL_LOCAIS: LocalArmazenamento[] = [
  { id: 'local-doca-recebimento', codigo_local: 'DOCAS', tipo: 'geral', localizacao: 'Área de Descarga e Conferência' },
  { id: 'local-corredor-m1', codigo_local: 'CORREDOR-M1', tipo: 'corredor', localizacao: 'Ala Lateral - Madeiras', capacidade_maxima: 50.0, unidade_capacidade: 'm3' },
  { id: 'local-gaiola-t1', codigo_local: 'GAIOLA-T1', tipo: 'gaiola', localizacao: 'Almoxarifado Tecidos', capacidade_maxima: 1000.0, unidade_capacidade: 'ml' },
  { id: 'local-gaiola-t2', codigo_local: 'GAIOLA-T2', tipo: 'gaiola', localizacao: 'Almoxarifado Tecidos', capacidade_maxima: 800.0, unidade_capacidade: 'ml' },
  { id: 'local-prateleira-f1', codigo_local: 'PRAT-F1', tipo: 'prateleira', localizacao: 'Almoxarifado Ferragens', capacidade_maxima: 15000.0, unidade_capacidade: 'un' }
]

export const INITIAL_LOTES: Lote[] = []

export const INITIAL_MOVIMENTACOES: Movimentacao[] = []

export const INITIAL_RESERVAS: Reserva[] = []

export const INITIAL_ITENS_OPERACIONAIS: ItemOperacional[] = []

export const INITIAL_VALES: Vale[] = []

export interface ComponenteReceita {
  produto_id: string;
  nome_exibicao: string;
  quantidade: number;
  unidade: string;
  volume_m3_unitario?: number;
  m2_unitario?: number;
}

export interface ReceitaCama {
  modelo: string;
  tipo: 'BOX' | 'BAÚ';
  custo_target: number;
  madeira: ComponenteReceita[];
  tecido: ComponenteReceita[];
  tnt: ComponenteReceita[];
  ferragens: ComponenteReceita[];
  acabamento: ComponenteReceita[];
  embalagem: ComponenteReceita[];
}

export const RECEITAS_CAMAS: ReceitaCama[] = [
  {
    modelo: '1,38x1,88',
    tipo: 'BOX',
    custo_target: 94.08,
    madeira: [
      { produto_id: 'prod-mad-chapa-138', nome_exibicao: 'Chapa Compensado 186x136x0.5cm', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Toco Pinus 21x5x2cm', quantidade: 6, unidade: 'un', volume_m3_unitario: 0.000210 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Toco Pinus 23x5x2cm', quantidade: 16, unidade: 'un', volume_m3_unitario: 0.000230 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 127x5x2cm', quantidade: 4, unidade: 'un', volume_m3_unitario: 0.001270 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 132,5x5x2cm', quantidade: 5, unidade: 'un', volume_m3_unitario: 0.001325 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 183x5x2cm', quantidade: 2, unidade: 'un', volume_m3_unitario: 0.001830 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 187x5x2cm', quantidade: 2, unidade: 'un', volume_m3_unitario: 0.001870 }
    ],
    tecido: [
      { produto_id: 'prod-tec-suede-bege', nome_exibicao: 'Faixa Lateral 652x35cm', quantidade: 1, unidade: 'un', m2_unitario: 2.282 }
    ],
    tnt: [
      { produto_id: 'prod-tnt-60-140', nome_exibicao: 'TNT 60" 140cm Tampo', quantidade: 1.88, unidade: 'ml', m2_unitario: 2.632 },
      { produto_id: 'prod-tnt-40-140', nome_exibicao: 'TNT 40" 140cm Fundo', quantidade: 1.88, unidade: 'ml', m2_unitario: 2.632 }
    ],
    ferragens: [
      { produto_id: 'prod-fer-grampo-1440', nome_exibicao: 'Grampo 14/40 Esqueleto', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-grampo-8008', nome_exibicao: 'Grampo 80/08 Tapeçaria', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-grampo-9220', nome_exibicao: 'Grampo 92/20 Chaparia', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-bucha', nome_exibicao: 'Bucha Americana Metal 2cm', quantidade: 6, unidade: 'un' },
      { produto_id: 'prod-fer-pe-12', nome_exibicao: 'Pé de Cama 12cm Plástico', quantidade: 6, unidade: 'un' }
    ],
    acabamento: [],
    embalagem: [
      { produto_id: 'prod-emb-manual', nome_exibicao: 'Manual de Instruções', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mal-600', nome_exibicao: 'Malha 600mm Revestimento', quantidade: 0.5, unidade: 'kg' },
      { produto_id: 'prod-emb-saco', nome_exibicao: 'Saco de Kit de Acessórios', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-emb-cantoneira-pap', nome_exibicao: 'Cantoneira de Papelão Proteção', quantidade: 4, unidade: 'un' }
    ]
  },
  {
    modelo: '1,38x1,88',
    tipo: 'BAÚ',
    custo_target: 200.77,
    madeira: [
      { produto_id: 'prod-mad-chapa-127', nome_exibicao: 'Chapa Compensado 175x127x0.5cm', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mad-chapa-138', nome_exibicao: 'Chapa Compensado 186x136x0.5cm', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Toco Pinus 25x5x2cm', quantidade: 20, unidade: 'un', volume_m3_unitario: 0.000250 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 100x7x2cm', quantidade: 4, unidade: 'un', volume_m3_unitario: 0.001400 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 127x5x2cm', quantidade: 4, unidade: 'un', volume_m3_unitario: 0.001270 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 132,5x5x2cm', quantidade: 6, unidade: 'un', volume_m3_unitario: 0.001325 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 137x5x2cm', quantidade: 3, unidade: 'un', volume_m3_unitario: 0.001370 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 187x5x2cm', quantidade: 2, unidade: 'un', volume_m3_unitario: 0.001870 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Trava Pinus', quantidade: 1, unidade: 'un', volume_m3_unitario: 0.002100 }
    ],
    tecido: [
      { produto_id: 'prod-tec-suede-bege', nome_exibicao: 'Faixa Base 652x38cm', quantidade: 1, unidade: 'un', m2_unitario: 2.4776 },
      { produto_id: 'prod-tec-suede-bege', nome_exibicao: 'Faixa Tampa 652x14cm', quantidade: 1, unidade: 'un', m2_unitario: 0.9128 }
    ],
    tnt: [
      { produto_id: 'prod-tnt-60-140', nome_exibicao: 'TNT 60" 140cm Tampo', quantidade: 1.88, unidade: 'ml', m2_unitario: 2.632 },
      { produto_id: 'prod-tnt-60-140', nome_exibicao: 'TNT 60" 140cm Interna Baú', quantidade: 1.88, unidade: 'ml', m2_unitario: 2.632 },
      { produto_id: 'prod-tnt-40-140', nome_exibicao: 'TNT 40" 140cm Interna Tampo', quantidade: 1.88, unidade: 'ml', m2_unitario: 2.632 },
      { produto_id: 'prod-tnt-40-140', nome_exibicao: 'TNT 40" 140cm Fundo Baú', quantidade: 1.88, unidade: 'ml', m2_unitario: 2.632 },
      { produto_id: 'prod-tnt-60-35', nome_exibicao: 'TNT 60" 35cm Faixa Interna', quantidade: 6.52, unidade: 'ml', m2_unitario: 2.282 }
    ],
    ferragens: [
      { produto_id: 'prod-fer-grampo-1440', nome_exibicao: 'Grampo 14/40 Esqueleto', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-grampo-8008', nome_exibicao: 'Grampo 80/08 Tapeçaria', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-grampo-9220', nome_exibicao: 'Grampo 92/20 Chaparia', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-articulacao', nome_exibicao: 'Articulação (par) Baú', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-fer-pistao', nome_exibicao: 'Pistão amortecedor Baú', quantidade: 2, unidade: 'un' },
      { produto_id: 'prod-fer-parafuso-5030', nome_exibicao: 'Parafuso 5,0x30 Estrutural', quantidade: 16, unidade: 'un' },
      { produto_id: 'prod-fer-parafuso-3525', nome_exibicao: 'Parafuso 3,5x25 Chaparia', quantidade: 6, unidade: 'un' },
      { produto_id: 'prod-fer-arco', nome_exibicao: 'Arco de Suporte Colchão', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-fer-bucha', nome_exibicao: 'Bucha Americana Metal 2cm', quantidade: 6, unidade: 'un' },
      { produto_id: 'prod-fer-pe-6', nome_exibicao: 'Pé de Cama 6cm Plástico', quantidade: 6, unidade: 'un' },
      { produto_id: 'prod-fer-cantoneira-pl', nome_exibicao: 'Cantoneira Plástica Estética', quantidade: 4, unidade: 'un' },
      { produto_id: 'prod-fer-puxador', nome_exibicao: 'Puxador de Plástico Cama Baú', quantidade: 2, unidade: 'un' }
    ],
    acabamento: [
      { produto_id: 'prod-col-tambor', nome_exibicao: 'Cola Spray/Tambor', quantidade: 0.007, unidade: 'kg' },
      { produto_id: 'prod-acab-lixa', nome_exibicao: 'Lixa de Acabamento', quantidade: 0.1, unidade: 'un' }
    ],
    embalagem: [
      { produto_id: 'prod-emb-manual', nome_exibicao: 'Manual de Instruções', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mal-600', nome_exibicao: 'Malha 600mm Revestimento', quantidade: 0.5, unidade: 'kg' },
      { produto_id: 'prod-emb-saco', nome_exibicao: 'Saco de Kit de Acessórios', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-emb-lateral-pap', nome_exibicao: 'Lateral de Papelão Protetora', quantidade: 2, unidade: 'un' }
    ]
  },
  {
    modelo: '1,28x1,88',
    tipo: 'BOX',
    custo_target: 92.50,
    madeira: [
      { produto_id: 'prod-mad-chapa-128', nome_exibicao: 'Chapa Compensado 186x126x0.5cm', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Toco Pinus 21x5x2cm', quantidade: 6, unidade: 'un', volume_m3_unitario: 0.000210 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Toco Pinus 23x5x2cm', quantidade: 16, unidade: 'un', volume_m3_unitario: 0.000230 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 117x5x2cm', quantidade: 4, unidade: 'un', volume_m3_unitario: 0.001170 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 123x5x2cm', quantidade: 5, unidade: 'un', volume_m3_unitario: 0.001230 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 183x5x2cm', quantidade: 2, unidade: 'un', volume_m3_unitario: 0.001830 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 187x5x2cm', quantidade: 2, unidade: 'un', volume_m3_unitario: 0.001870 }
    ],
    tecido: [
      { produto_id: 'prod-tec-suede-bege', nome_exibicao: 'Faixa Lateral 632x35cm', quantidade: 1, unidade: 'un', m2_unitario: 2.212 }
    ],
    tnt: [
      { produto_id: 'prod-tnt-60-140', nome_exibicao: 'TNT 60" 140cm Tampo', quantidade: 1.88, unidade: 'ml', m2_unitario: 2.632 },
      { produto_id: 'prod-tnt-40-140', nome_exibicao: 'TNT 40" 140cm Fundo', quantidade: 1.88, unidade: 'ml', m2_unitario: 2.632 }
    ],
    ferragens: [
      { produto_id: 'prod-fer-grampo-1440', nome_exibicao: 'Grampo 14/40 Esqueleto', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-grampo-8008', nome_exibicao: 'Grampo 80/08 Tapeçaria', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-grampo-9220', nome_exibicao: 'Grampo 92/20 Chaparia', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-bucha', nome_exibicao: 'Bucha Americana Metal 2cm', quantidade: 6, unidade: 'un' },
      { produto_id: 'prod-fer-pe-12', nome_exibicao: 'Pé de Cama 12cm Plástico', quantidade: 6, unidade: 'un' }
    ],
    acabamento: [],
    embalagem: [
      { produto_id: 'prod-emb-manual', nome_exibicao: 'Manual de Instruções', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mal-600', nome_exibicao: 'Malha 600mm Revestimento', quantidade: 0.5, unidade: 'kg' },
      { produto_id: 'prod-emb-saco', nome_exibicao: 'Saco de Kit de Acessórios', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-emb-cantoneira-pap', nome_exibicao: 'Cantoneira de Papelão Proteção', quantidade: 4, unidade: 'un' }
    ]
  },
  {
    modelo: '1,28x1,88',
    tipo: 'BAÚ',
    custo_target: 198.34,
    madeira: [
      { produto_id: 'prod-mad-chapa-117', nome_exibicao: 'Chapa Compensado 175x117x0.5cm', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mad-chapa-128', nome_exibicao: 'Chapa Compensado 186x126x0.5cm', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Toco Pinus 25x5x2cm', quantidade: 20, unidade: 'un', volume_m3_unitario: 0.000250 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 100x7x2cm', quantidade: 4, unidade: 'un', volume_m3_unitario: 0.001400 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 117x5x2cm', quantidade: 4, unidade: 'un', volume_m3_unitario: 0.001170 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 122,5x5x2cm', quantidade: 6, unidade: 'un', volume_m3_unitario: 0.001225 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 127x5x2cm', quantidade: 3, unidade: 'un', volume_m3_unitario: 0.001270 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 187x5x2cm', quantidade: 2, unidade: 'un', volume_m3_unitario: 0.001870 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Trava Pinus', quantidade: 1, unidade: 'un', volume_m3_unitario: 0.002100 }
    ],
    tecido: [
      { produto_id: 'prod-tec-suede-bege', nome_exibicao: 'Faixa Base 632x38cm', quantidade: 1, unidade: 'un', m2_unitario: 2.4016 },
      { produto_id: 'prod-tec-suede-bege', nome_exibicao: 'Faixa Tampa 632x14cm', quantidade: 1, unidade: 'un', m2_unitario: 0.8848 }
    ],
    tnt: [
      { produto_id: 'prod-tnt-60-140', nome_exibicao: 'TNT 60" 140cm Tampo', quantidade: 1.88, unidade: 'ml', m2_unitario: 2.632 },
      { produto_id: 'prod-tnt-60-140', nome_exibicao: 'TNT 60" 140cm Interna Baú', quantidade: 1.88, unidade: 'ml', m2_unitario: 2.632 },
      { produto_id: 'prod-tnt-40-140', nome_exibicao: 'TNT 40" 140cm Interna Tampo', quantidade: 1.88, unidade: 'ml', m2_unitario: 2.632 },
      { produto_id: 'prod-tnt-40-140', nome_exibicao: 'TNT 40" 140cm Fundo Baú', quantidade: 1.88, unidade: 'ml', m2_unitario: 2.632 },
      { produto_id: 'prod-tnt-60-35', nome_exibicao: 'TNT 60" 35cm Faixa Interna', quantidade: 6.32, unidade: 'ml', m2_unitario: 2.212 }
    ],
    ferragens: [
      { produto_id: 'prod-fer-grampo-1440', nome_exibicao: 'Grampo 14/40 Esqueleto', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-grampo-8008', nome_exibicao: 'Grampo 80/08 Tapeçaria', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-grampo-9220', nome_exibicao: 'Grampo 92/20 Chaparia', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-articulacao', nome_exibicao: 'Articulação (par) Baú', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-fer-pistao', nome_exibicao: 'Pistão amortecedor Baú', quantidade: 2, unidade: 'un' },
      { produto_id: 'prod-fer-parafuso-5030', nome_exibicao: 'Parafuso 5,0x30 Estrutural', quantidade: 16, unidade: 'un' },
      { produto_id: 'prod-fer-parafuso-3525', nome_exibicao: 'Parafuso 3,5x25 Chaparia', quantidade: 6, unidade: 'un' },
      { produto_id: 'prod-fer-arco', nome_exibicao: 'Arco de Suporte Colchão', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-fer-bucha', nome_exibicao: 'Bucha Americana Metal 2cm', quantidade: 6, unidade: 'un' },
      { produto_id: 'prod-fer-pe-6', nome_exibicao: 'Pé de Cama 6cm Plástico', quantidade: 6, unidade: 'un' },
      { produto_id: 'prod-fer-cantoneira-pl', nome_exibicao: 'Cantoneira Plástica Estética', quantidade: 4, unidade: 'un' },
      { produto_id: 'prod-fer-puxador', nome_exibicao: 'Puxador de Plástico Cama Baú', quantidade: 2, unidade: 'un' }
    ],
    acabamento: [
      { produto_id: 'prod-col-tambor', nome_exibicao: 'Cola Spray/Tambor', quantidade: 0.007, unidade: 'kg' },
      { produto_id: 'prod-acab-lixa', nome_exibicao: 'Lixa de Acabamento', quantidade: 0.1, unidade: 'un' }
    ],
    embalagem: [
      { produto_id: 'prod-emb-manual', nome_exibicao: 'Manual de Instruções', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mal-600', nome_exibicao: 'Malha 600mm Revestimento', quantidade: 0.5, unidade: 'kg' },
      { produto_id: 'prod-emb-saco', nome_exibicao: 'Saco de Kit de Acessórios', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-emb-lateral-pap', nome_exibicao: 'Lateral de Papelão Protetora', quantidade: 2, unidade: 'un' }
    ]
  },
  {
    modelo: '0,96x2,03',
    tipo: 'BOX',
    custo_target: 88.50,
    madeira: [
      { produto_id: 'prod-mad-chapa-96', nome_exibicao: 'Chapa Compensado 201x94x0.5cm', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Toco Pinus 23x5x2cm', quantidade: 20, unidade: 'un', volume_m3_unitario: 0.000230 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 91x5x2cm', quantidade: 9, unidade: 'un', volume_m3_unitario: 0.000910 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 198x5x2cm', quantidade: 6, unidade: 'un', volume_m3_unitario: 0.001980 }
    ],
    tecido: [
      { produto_id: 'prod-tec-suede-bege', nome_exibicao: 'Faixa Lateral 598x35cm', quantidade: 1, unidade: 'un', m2_unitario: 2.093 }
    ],
    tnt: [
      { produto_id: 'prod-tnt-60-140', nome_exibicao: 'TNT 60" 140cm Tampo', quantidade: 2.03, unidade: 'ml', m2_unitario: 2.842 },
      { produto_id: 'prod-tnt-40-140', nome_exibicao: 'TNT 40" 140cm Fundo', quantidade: 2.03, unidade: 'ml', m2_unitario: 2.842 }
    ],
    ferragens: [
      { produto_id: 'prod-fer-grampo-1440', nome_exibicao: 'Grampo 14/40 Esqueleto', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-grampo-8008', nome_exibicao: 'Grampo 80/08 Tapeçaria', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-grampo-9220', nome_exibicao: 'Grampo 92/20 Chaparia', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-bucha', nome_exibicao: 'Bucha Americana Metal 2cm', quantidade: 6, unidade: 'un' },
      { produto_id: 'prod-fer-pe-12', nome_exibicao: 'Pé de Cama 12cm Plástico', quantidade: 6, unidade: 'un' },
      { produto_id: 'prod-fer-chapa-uniao', nome_exibicao: 'Chapa de União', quantidade: 1, unidade: 'un' }
    ],
    acabamento: [],
    embalagem: [
      { produto_id: 'prod-emb-manual', nome_exibicao: 'Manual de Instruções', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mal-600', nome_exibicao: 'Malha 600mm Revestimento', quantidade: 0.5, unidade: 'kg' },
      { produto_id: 'prod-emb-saco', nome_exibicao: 'Saco de Kit de Acessórios', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-emb-cantoneira-pap', nome_exibicao: 'Cantoneira de Papelão Proteção', quantidade: 4, unidade: 'un' }
    ]
  },
  {
    modelo: '0,96x2,03',
    tipo: 'BAÚ',
    custo_target: 196.20,
    madeira: [
      { produto_id: 'prod-mad-chapa-85', nome_exibicao: 'Chapa Compensado 190x85x0.5cm', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mad-chapa-96', nome_exibicao: 'Chapa Compensado 201x94x0.5cm', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Toco Pinus 25x5x2cm', quantidade: 22, unidade: 'un', volume_m3_unitario: 0.000250 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 70x7x2cm', quantidade: 2, unidade: 'un', volume_m3_unitario: 0.000980 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 80x7x2cm', quantidade: 1, unidade: 'un', volume_m3_unitario: 0.001120 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 91x5x2cm', quantidade: 4, unidade: 'un', volume_m3_unitario: 0.000910 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 95x5x2cm', quantidade: 9, unidade: 'un', volume_m3_unitario: 0.000950 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 100x7x2cm', quantidade: 4, unidade: 'un', volume_m3_unitario: 0.001400 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 192x5x2cm', quantidade: 4, unidade: 'un', volume_m3_unitario: 0.001920 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 198x5x2cm', quantidade: 2, unidade: 'un', volume_m3_unitario: 0.001980 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Trava Pinus', quantidade: 1, unidade: 'un', volume_m3_unitario: 0.002100 }
    ],
    tecido: [
      { produto_id: 'prod-tec-suede-bege', nome_exibicao: 'Faixa Base 598x38cm', quantidade: 1, unidade: 'un', m2_unitario: 2.2724 },
      { produto_id: 'prod-tec-suede-bege', nome_exibicao: 'Faixa Tampa 598x14cm', quantidade: 1, unidade: 'un', m2_unitario: 0.8372 }
    ],
    tnt: [
      { produto_id: 'prod-tnt-60-140', nome_exibicao: 'TNT 60" 140cm Tampo', quantidade: 2.03, unidade: 'ml', m2_unitario: 2.842 },
      { produto_id: 'prod-tnt-60-140', nome_exibicao: 'TNT 60" 140cm Interna Baú', quantidade: 2.03, unidade: 'ml', m2_unitario: 2.842 },
      { produto_id: 'prod-tnt-40-140', nome_exibicao: 'TNT 40" 140cm Interna Tampo', quantidade: 2.03, unidade: 'ml', m2_unitario: 2.842 },
      { produto_id: 'prod-tnt-40-140', nome_exibicao: 'TNT 40" 140cm Fundo Baú', quantidade: 2.03, unidade: 'ml', m2_unitario: 2.842 },
      { produto_id: 'prod-tnt-60-35', nome_exibicao: 'TNT 60" 35cm Faixa Interna', quantidade: 5.98, unidade: 'ml', m2_unitario: 2.093 }
    ],
    ferragens: [
      { produto_id: 'prod-fer-grampo-1440', nome_exibicao: 'Grampo 14/40 Esqueleto', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-grampo-8008', nome_exibicao: 'Grampo 80/08 Tapeçaria', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-grampo-9220', nome_exibicao: 'Grampo 92/20 Chaparia', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-articulacao', nome_exibicao: 'Articulação (par) Baú', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-fer-pistao', nome_exibicao: 'Pistão amortecedor Baú', quantidade: 2, unidade: 'un' },
      { produto_id: 'prod-fer-parafuso-5030', nome_exibicao: 'Parafuso 5,0x30 Estrutural', quantidade: 16, unidade: 'un' },
      { produto_id: 'prod-fer-parafuso-3525', nome_exibicao: 'Parafuso 3,5x25 Chaparia', quantidade: 4, unidade: 'un' },
      { produto_id: 'prod-fer-arco', nome_exibicao: 'Arco de Suporte Colchão', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-fer-bucha', nome_exibicao: 'Bucha Americana Metal 2cm', quantidade: 6, unidade: 'un' },
      { produto_id: 'prod-fer-pe-6', nome_exibicao: 'Pé de Cama 6cm Plástico', quantidade: 6, unidade: 'un' },
      { produto_id: 'prod-fer-cantoneira-pl', nome_exibicao: 'Cantoneira Plástica Estética', quantidade: 4, unidade: 'un' },
      { produto_id: 'prod-fer-chapa-uniao', nome_exibicao: 'Chapa de União', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-fer-puxador', nome_exibicao: 'Puxador de Plástico Cama Baú', quantidade: 1, unidade: 'un' }
    ],
    acabamento: [
      { produto_id: 'prod-col-tambor', nome_exibicao: 'Cola Spray/Tambor', quantidade: 0.007, unidade: 'kg' },
      { produto_id: 'prod-acab-lixa', nome_exibicao: 'Lixa de Acabamento', quantidade: 0.1, unidade: 'un' }
    ],
    embalagem: [
      { produto_id: 'prod-emb-manual', nome_exibicao: 'Manual de Instruções', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mal-600', nome_exibicao: 'Malha 600mm Revestimento', quantidade: 0.5, unidade: 'kg' },
      { produto_id: 'prod-emb-saco', nome_exibicao: 'Saco de Kit de Acessórios', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-emb-lateral-pap', nome_exibicao: 'Lateral de Papelão Protetora', quantidade: 2, unidade: 'un' }
    ]
  },
  {
    modelo: '0,88x1,88',
    tipo: 'BOX',
    custo_target: 78.38,
    madeira: [
      { produto_id: 'prod-mad-chapa-88', nome_exibicao: 'Chapa Compensado 186x86x0.5cm', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Toco Pinus 21x5x2cm', quantidade: 6, unidade: 'un', volume_m3_unitario: 0.000210 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Toco Pinus 23x5x2cm', quantidade: 16, unidade: 'un', volume_m3_unitario: 0.000230 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 78x5x2cm', quantidade: 4, unidade: 'un', volume_m3_unitario: 0.000780 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 83x5x2cm', quantidade: 5, unidade: 'un', volume_m3_unitario: 0.000830 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 183x5x2cm', quantidade: 2, unidade: 'un', volume_m3_unitario: 0.001830 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 187x5x2cm', quantidade: 2, unidade: 'un', volume_m3_unitario: 0.001870 }
    ],
    tecido: [
      { produto_id: 'prod-tec-suede-bege', nome_exibicao: 'Faixa Lateral 552x35cm', quantidade: 1, unidade: 'un', m2_unitario: 1.932 }
    ],
    tnt: [
      { produto_id: 'prod-tnt-60-140', nome_exibicao: 'TNT 60" 140cm Tampo', quantidade: 1.88, unidade: 'ml', m2_unitario: 2.632 },
      { produto_id: 'prod-tnt-40-140', nome_exibicao: 'TNT 40" 140cm Fundo', quantidade: 1.88, unidade: 'ml', m2_unitario: 2.632 }
    ],
    ferragens: [
      { produto_id: 'prod-fer-grampo-1440', nome_exibicao: 'Grampo 14/40 Esqueleto', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-grampo-8008', nome_exibicao: 'Grampo 80/08 Tapeçaria', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-grampo-9220', nome_exibicao: 'Grampo 92/20 Chaparia', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-bucha', nome_exibicao: 'Bucha Americana Metal 2cm', quantidade: 6, unidade: 'un' },
      { produto_id: 'prod-fer-pe-12', nome_exibicao: 'Pé de Cama 12cm Plástico', quantidade: 6, unidade: 'un' }
    ],
    acabamento: [],
    embalagem: [
      { produto_id: 'prod-emb-manual', nome_exibicao: 'Manual de Instruções', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mal-600', nome_exibicao: 'Malha 600mm Revestimento', quantidade: 0.5, unidade: 'kg' },
      { produto_id: 'prod-emb-saco', nome_exibicao: 'Saco de Kit de Acessórios', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-emb-cantoneira-pap', nome_exibicao: 'Cantoneira de Papelão Proteção', quantidade: 4, unidade: 'un' }
    ]
  },
  {
    modelo: '0,88x1,88',
    tipo: 'BAÚ',
    custo_target: 179.94,
    madeira: [
      { produto_id: 'prod-mad-chapa-75', nome_exibicao: 'Chapa Compensado 175x75x0.5cm', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mad-chapa-88', nome_exibicao: 'Chapa Compensado 186x86x0.5cm', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Toco Pinus 25x5x2cm', quantidade: 22, unidade: 'un', volume_m3_unitario: 0.000250 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 70x7x2cm', quantidade: 4, unidade: 'un', volume_m3_unitario: 0.000980 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 78x5x2cm', quantidade: 4, unidade: 'un', volume_m3_unitario: 0.000780 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 83,5x5x2cm', quantidade: 5, unidade: 'un', volume_m3_unitario: 0.000835 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 87x5x2cm', quantidade: 3, unidade: 'un', volume_m3_unitario: 0.000870 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 187x5x2cm', quantidade: 6, unidade: 'un', volume_m3_unitario: 0.001870 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Trava Pinus', quantidade: 1, unidade: 'un', volume_m3_unitario: 0.002100 }
    ],
    tecido: [
      { produto_id: 'prod-tec-suede-bege', nome_exibicao: 'Faixa Base 552x38cm', quantidade: 1, unidade: 'un', m2_unitario: 2.0976 },
      { produto_id: 'prod-tec-suede-bege', nome_exibicao: 'Faixa Tampa 552x14cm', quantidade: 1, unidade: 'un', m2_unitario: 0.7728 }
    ],
    tnt: [
      { produto_id: 'prod-tnt-60-140', nome_exibicao: 'TNT 60" 140cm Tampo', quantidade: 1.88, unidade: 'ml', m2_unitario: 2.632 },
      { produto_id: 'prod-tnt-60-140', nome_exibicao: 'TNT 60" 140cm Interna Baú', quantidade: 1.88, unidade: 'ml', m2_unitario: 2.632 },
      { produto_id: 'prod-tnt-40-140', nome_exibicao: 'TNT 40" 140cm Interna Tampo', quantidade: 1.88, unidade: 'ml', m2_unitario: 2.632 },
      { produto_id: 'prod-tnt-40-140', nome_exibicao: 'TNT 40" 140cm Fundo Baú', quantidade: 1.88, unidade: 'ml', m2_unitario: 2.632 },
      { produto_id: 'prod-tnt-60-35', nome_exibicao: 'TNT 60" 35cm Faixa Interna', quantidade: 5.52, unidade: 'ml', m2_unitario: 1.932 }
    ],
    ferragens: [
      { produto_id: 'prod-fer-grampo-1440', nome_exibicao: 'Grampo 14/40 Esqueleto', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-grampo-8008', nome_exibicao: 'Grampo 80/08 Tapeçaria', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-grampo-9220', nome_exibicao: 'Grampo 92/20 Chaparia', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-articulacao', nome_exibicao: 'Articulação (par) Baú', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-fer-pistao', nome_exibicao: 'Pistão amortecedor Baú', quantidade: 2, unidade: 'un' },
      { produto_id: 'prod-fer-parafuso-5030', nome_exibicao: 'Parafuso 5,0x30 Estrutural', quantidade: 16, unidade: 'un' },
      { produto_id: 'prod-fer-parafuso-3525', nome_exibicao: 'Parafuso 3,5x25 Chaparia', quantidade: 4, unidade: 'un' },
      { produto_id: 'prod-fer-arco', nome_exibicao: 'Arco de Suporte Colchão', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-fer-bucha', nome_exibicao: 'Bucha Americana Metal 2cm', quantidade: 6, unidade: 'un' },
      { produto_id: 'prod-fer-pe-6', nome_exibicao: 'Pé de Cama 6cm Plástico', quantidade: 6, unidade: 'un' },
      { produto_id: 'prod-fer-cantoneira-pl', nome_exibicao: 'Cantoneira Plástica Estética', quantidade: 4, unidade: 'un' },
      { produto_id: 'prod-fer-puxador', nome_exibicao: 'Puxador de Plástico Cama Baú', quantidade: 1, unidade: 'un' }
    ],
    acabamento: [
      { produto_id: 'prod-col-tambor', nome_exibicao: 'Cola Spray/Tambor', quantidade: 0.007, unidade: 'kg' },
      { produto_id: 'prod-acab-lixa', nome_exibicao: 'Lixa de Acabamento', quantidade: 0.1, unidade: 'un' }
    ],
    embalagem: [
      { produto_id: 'prod-emb-manual', nome_exibicao: 'Manual de Instruções', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mal-600', nome_exibicao: 'Malha 600mm Revestimento', quantidade: 0.5, unidade: 'kg' },
      { produto_id: 'prod-emb-saco', nome_exibicao: 'Saco de Kit de Acessórios', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-emb-lateral-pap', nome_exibicao: 'Lateral de Papelão Protetora', quantidade: 2, unidade: 'un' }
    ]
  },
  {
    modelo: '0,79x1,98',
    tipo: 'BOX',
    custo_target: 81.66,
    madeira: [
      { produto_id: 'prod-mad-chapa-79', nome_exibicao: 'Chapa Compensado 196x77x0.5cm', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Toco Pinus 23x5x2cm', quantidade: 18, unidade: 'un', volume_m3_unitario: 0.000230 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 74x5x2cm', quantidade: 9, unidade: 'un', volume_m3_unitario: 0.000740 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 192x5x2cm', quantidade: 6, unidade: 'un', volume_m3_unitario: 0.001920 }
    ],
    tecido: [
      { produto_id: 'prod-tec-suede-bege', nome_exibicao: 'Faixa Lateral 554x35cm', quantidade: 1, unidade: 'un', m2_unitario: 1.939 }
    ],
    tnt: [
      { produto_id: 'prod-tnt-60-140', nome_exibicao: 'TNT 60" 140cm Tampo', quantidade: 1.98, unidade: 'ml', m2_unitario: 2.772 },
      { produto_id: 'prod-tnt-40-140', nome_exibicao: 'TNT 40" 140cm Fundo', quantidade: 1.98, unidade: 'ml', m2_unitario: 2.772 }
    ],
    ferragens: [
      { produto_id: 'prod-fer-grampo-1440', nome_exibicao: 'Grampo 14/40 Esqueleto', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-grampo-8008', nome_exibicao: 'Grampo 80/08 Tapeçaria', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-grampo-9220', nome_exibicao: 'Grampo 92/20 Chaparia', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-bucha', nome_exibicao: 'Bucha Americana Metal 2cm', quantidade: 6, unidade: 'un' },
      { produto_id: 'prod-fer-pe-12', nome_exibicao: 'Pé de Cama 12cm Plástico', quantidade: 6, unidade: 'un' },
      { produto_id: 'prod-fer-chapa-uniao', nome_exibicao: 'Chapa de União', quantidade: 1, unidade: 'un' }
    ],
    acabamento: [],
    embalagem: [
      { produto_id: 'prod-emb-manual', nome_exibicao: 'Manual de Instruções', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mal-600', nome_exibicao: 'Malha 600mm Revestimento', quantidade: 0.5, unidade: 'kg' },
      { produto_id: 'prod-emb-saco', nome_exibicao: 'Saco de Kit de Acessórios', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-emb-cantoneira-pap', nome_exibicao: 'Cantoneira de Papelão Proteção', quantidade: 4, unidade: 'un' }
    ]
  },
  {
    modelo: '0,79x1,98',
    tipo: 'BAÚ',
    custo_target: 167.38,
    madeira: [
      { produto_id: 'prod-mad-chapa-66', nome_exibicao: 'Chapa Compensado 186x66x0.5cm', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mad-chapa-79', nome_exibicao: 'Chapa Compensado 196x77x0.5cm', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Toco Pinus 25x5x2cm', quantidade: 16, unidade: 'un', volume_m3_unitario: 0.000250 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 73,5x5x2cm', quantidade: 4, unidade: 'un', volume_m3_unitario: 0.000735 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 78x5x2cm', quantidade: 2, unidade: 'un', volume_m3_unitario: 0.000780 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 87x5x2cm', quantidade: 4, unidade: 'un', volume_m3_unitario: 0.000870 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 100x7x2cm', quantidade: 4, unidade: 'un', volume_m3_unitario: 0.001400 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 192x5x2cm', quantidade: 2, unidade: 'un', volume_m3_unitario: 0.001920 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Trava Pinus', quantidade: 1, unidade: 'un', volume_m3_unitario: 0.002100 }
    ],
    tecido: [
      { produto_id: 'prod-tec-suede-bege', nome_exibicao: 'Faixa Base 554x38cm', quantidade: 1, unidade: 'un', m2_unitario: 2.1052 },
      { produto_id: 'prod-tec-suede-bege', nome_exibicao: 'Faixa Tampa 554x14cm', quantidade: 1, unidade: 'un', m2_unitario: 0.7756 }
    ],
    tnt: [
      { produto_id: 'prod-tnt-60-140', nome_exibicao: 'TNT 60" 140cm Tampo', quantidade: 1.98, unidade: 'ml', m2_unitario: 2.772 },
      { produto_id: 'prod-tnt-60-140', nome_exibicao: 'TNT 60" 140cm Interna Baú', quantidade: 1.98, unidade: 'ml', m2_unitario: 2.772 },
      { produto_id: 'prod-tnt-40-140', nome_exibicao: 'TNT 40" 140cm Interna Tampo', quantidade: 1.98, unidade: 'ml', m2_unitario: 2.772 },
      { produto_id: 'prod-tnt-40-140', nome_exibicao: 'TNT 40" 140cm Fundo Baú', quantidade: 1.98, unidade: 'ml', m2_unitario: 2.632 },
      { produto_id: 'prod-tnt-60-35', nome_exibicao: 'TNT 60" 35cm Faixa Interna', quantidade: 5.54, unidade: 'ml', m2_unitario: 1.939 }
    ],
    ferragens: [
      { produto_id: 'prod-fer-grampo-1440', nome_exibicao: 'Grampo 14/40 Esqueleto', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-grampo-8008', nome_exibicao: 'Grampo 80/08 Tapeçaria', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-grampo-9220', nome_exibicao: 'Grampo 92/20 Chaparia', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-articulacao', nome_exibicao: 'Articulação (par) Baú', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-fer-pistao', nome_exibicao: 'Pistão amortecedor Baú', quantidade: 2, unidade: 'un' },
      { produto_id: 'prod-fer-parafuso-5030', nome_exibicao: 'Parafuso 5,0x30 Estrutural', quantidade: 16, unidade: 'un' },
      { produto_id: 'prod-fer-parafuso-3525', nome_exibicao: 'Parafuso 3,5x25 Chaparia', quantidade: 4, unidade: 'un' },
      { produto_id: 'prod-fer-arco', nome_exibicao: 'Arco de Suporte Colchão', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-fer-bucha', nome_exibicao: 'Bucha Americana Metal 2cm', quantidade: 6, unidade: 'un' },
      { produto_id: 'prod-fer-pe-6', nome_exibicao: 'Pé de Cama 6cm Plástico', quantidade: 6, unidade: 'un' },
      { produto_id: 'prod-fer-cantoneira-pl', nome_exibicao: 'Cantoneira Plástica Estética', quantidade: 4, unidade: 'un' },
      { produto_id: 'prod-fer-chapa-uniao', nome_exibicao: 'Chapa de União', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-fer-puxador', nome_exibicao: 'Puxador de Plástico Cama Baú', quantidade: 1, unidade: 'un' }
    ],
    acabamento: [
      { produto_id: 'prod-col-tambor', nome_exibicao: 'Cola Spray/Tambor', quantidade: 0.007, unidade: 'kg' },
      { produto_id: 'prod-acab-lixa', nome_exibicao: 'Lixa de Acabamento', quantidade: 0.1, unidade: 'un' }
    ],
    embalagem: [
      { produto_id: 'prod-emb-manual', nome_exibicao: 'Manual de Instruções', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mal-600', nome_exibicao: 'Malha 600mm Revestimento', quantidade: 0.5, unidade: 'kg' },
      { produto_id: 'prod-emb-saco', nome_exibicao: 'Saco de Kit de Acessórios', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-emb-lateral-pap', nome_exibicao: 'Lateral de Papelão Protetora', quantidade: 2, unidade: 'un' }
    ]
  },
  {
    modelo: '0,78x1,88',
    tipo: 'BOX',
    custo_target: 73.54,
    madeira: [
      { produto_id: 'prod-mad-chapa-79', nome_exibicao: 'Chapa Compensado 196x77x0.5cm', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Toco Pinus 21x5x2cm', quantidade: 6, unidade: 'un', volume_m3_unitario: 0.000210 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Toco Pinus 23x5x2cm', quantidade: 16, unidade: 'un', volume_m3_unitario: 0.000230 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 68x5x2cm', quantidade: 4, unidade: 'un', volume_m3_unitario: 0.000680 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 74x5x2cm', quantidade: 5, unidade: 'un', volume_m3_unitario: 0.000740 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 87x5x2cm', quantidade: 2, unidade: 'un', volume_m3_unitario: 0.000870 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 183x5x2cm', quantidade: 2, unidade: 'un', volume_m3_unitario: 0.001830 }
    ],
    tecido: [
      { produto_id: 'prod-tec-suede-bege', nome_exibicao: 'Faixa Lateral 532x35cm', quantidade: 1, unidade: 'un', m2_unitario: 1.862 }
    ],
    tnt: [
      { produto_id: 'prod-tnt-60-140', nome_exibicao: 'TNT 60" 140cm Tampo', quantidade: 1.88, unidade: 'ml', m2_unitario: 2.632 },
      { produto_id: 'prod-tnt-40-140', nome_exibicao: 'TNT 40" 140cm Fundo', quantidade: 1.88, unidade: 'ml', m2_unitario: 2.632 }
    ],
    ferragens: [
      { produto_id: 'prod-fer-grampo-1440', nome_exibicao: 'Grampo 14/40 Esqueleto', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-grampo-8008', nome_exibicao: 'Grampo 80/08 Tapeçaria', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-grampo-9220', nome_exibicao: 'Grampo 92/20 Chaparia', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-bucha', nome_exibicao: 'Bucha Americana Metal 2cm', quantidade: 6, unidade: 'un' },
      { produto_id: 'prod-fer-pe-12', nome_exibicao: 'Pé de Cama 12cm Plástico', quantidade: 6, unidade: 'un' }
    ],
    acabamento: [],
    embalagem: [
      { produto_id: 'prod-emb-manual', nome_exibicao: 'Manual de Instruções', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mal-600', nome_exibicao: 'Malha 600mm Revestimento', quantidade: 0.5, unidade: 'kg' },
      { produto_id: 'prod-emb-saco', nome_exibicao: 'Saco de Kit de Acessórios', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-emb-cantoneira-pap', nome_exibicao: 'Cantoneira de Papelão Proteção', quantidade: 4, unidade: 'un' }
    ]
  },
  {
    modelo: '0,78x1,88',
    tipo: 'BAÚ',
    custo_target: 166.92,
    madeira: [
      { produto_id: 'prod-mad-chapa-66-bau', nome_exibicao: 'Chapa Compensado 175x66x0.5cm', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mad-chapa-79', nome_exibicao: 'Chapa Compensado 196x77x0.5cm', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Toco Pinus 25x5x2cm', quantidade: 22, unidade: 'un', volume_m3_unitario: 0.000250 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 60x5x2cm', quantidade: 2, unidade: 'un', volume_m3_unitario: 0.000600 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 68x5x2cm', quantidade: 4, unidade: 'un', volume_m3_unitario: 0.000680 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 73,5x5x2cm', quantidade: 4, unidade: 'un', volume_m3_unitario: 0.000735 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 78x5x2cm', quantidade: 3, unidade: 'un', volume_m3_unitario: 0.000780 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 187x5x2cm', quantidade: 4, unidade: 'un', volume_m3_unitario: 0.001870 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Trava Pinus', quantidade: 1, unidade: 'un', volume_m3_unitario: 0.002100 }
    ],
    tecido: [
      { produto_id: 'prod-tec-suede-bege', nome_exibicao: 'Faixa Base 532x38cm', quantidade: 1, unidade: 'un', m2_unitario: 2.0216 },
      { produto_id: 'prod-tec-suede-bege', nome_exibicao: 'Faixa Tampa 532x14cm', quantidade: 1, unidade: 'un', m2_unitario: 0.7448 }
    ],
    tnt: [
      { produto_id: 'prod-tnt-60-140', nome_exibicao: 'TNT 60" 140cm Tampo', quantidade: 1.88, unidade: 'ml', m2_unitario: 2.632 },
      { produto_id: 'prod-tnt-60-140', nome_exibicao: 'TNT 60" 140cm Interna Baú', quantidade: 1.88, unidade: 'ml', m2_unitario: 2.632 },
      { produto_id: 'prod-tnt-40-140', nome_exibicao: 'TNT 40" 140cm Interna Tampo', quantidade: 1.88, unidade: 'ml', m2_unitario: 2.632 },
      { produto_id: 'prod-tnt-40-140', nome_exibicao: 'TNT 40" 140cm Fundo Baú', quantidade: 1.88, unidade: 'ml', m2_unitario: 2.632 },
      { produto_id: 'prod-tnt-60-35', nome_exibicao: 'TNT 60" 35cm Faixa Interna', quantidade: 5.32, unidade: 'ml', m2_unitario: 1.862 }
    ],
    ferragens: [
      { produto_id: 'prod-fer-grampo-1440', nome_exibicao: 'Grampo 14/40 Esqueleto', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-grampo-8008', nome_exibicao: 'Grampo 80/08 Tapeçaria', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-grampo-9220', nome_exibicao: 'Grampo 92/20 Chaparia', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-articulacao', nome_exibicao: 'Articulação (par) Baú', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-fer-pistao', nome_exibicao: 'Pistão amortecedor Baú', quantidade: 2, unidade: 'un' },
      { produto_id: 'prod-fer-parafuso-5030', nome_exibicao: 'Parafuso 5,0x30 Estrutural', quantidade: 16, unidade: 'un' },
      { produto_id: 'prod-fer-parafuso-3525', nome_exibicao: 'Parafuso 3,5x25 Chaparia', quantidade: 4, unidade: 'un' },
      { produto_id: 'prod-fer-arco', nome_exibicao: 'Arco de Suporte Colchão', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-fer-bucha', nome_exibicao: 'Bucha Americana Metal 2cm', quantidade: 6, unidade: 'un' },
      { produto_id: 'prod-fer-pe-6', nome_exibicao: 'Pé de Cama 6cm Plástico', quantidade: 6, unidade: 'un' },
      { produto_id: 'prod-fer-cantoneira-pl', nome_exibicao: 'Cantoneira Plástica Estética', quantidade: 4, unidade: 'un' },
      { produto_id: 'prod-fer-puxador', nome_exibicao: 'Puxador de Plástico Cama Baú', quantidade: 1, unidade: 'un' }
    ],
    acabamento: [
      { produto_id: 'prod-col-tambor', nome_exibicao: 'Cola Spray/Tambor', quantidade: 0.007, unidade: 'kg' },
      { produto_id: 'prod-acab-lixa', nome_exibicao: 'Lixa de Acabamento', quantidade: 0.1, unidade: 'un' }
    ],
    embalagem: [
      { produto_id: 'prod-emb-manual', nome_exibicao: 'Manual de Instruções', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mal-600', nome_exibicao: 'Malha 600mm Revestimento', quantidade: 0.5, unidade: 'kg' },
      { produto_id: 'prod-emb-saco', nome_exibicao: 'Saco de Kit de Acessórios', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-emb-lateral-pap', nome_exibicao: 'Lateral de Papelão Protetora', quantidade: 2, unidade: 'un' }
    ]
  },
  {
    modelo: '0,69x1,88',
    tipo: 'BOX',
    custo_target: 73.07,
    madeira: [
      { produto_id: 'prod-mad-chapa-69', nome_exibicao: 'Chapa Compensado 186x66x0.5cm', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Toco Pinus 21x5x2cm', quantidade: 6, unidade: 'un', volume_m3_unitario: 0.000210 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Toco Pinus 23x5x2cm', quantidade: 16, unidade: 'un', volume_m3_unitario: 0.000230 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 58x5x2cm', quantidade: 4, unidade: 'un', volume_m3_unitario: 0.000580 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 64x5x2cm', quantidade: 5, unidade: 'un', volume_m3_unitario: 0.000640 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 183x5x2cm', quantidade: 2, unidade: 'un', volume_m3_unitario: 0.001830 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 187x5x2cm', quantidade: 2, unidade: 'un', volume_m3_unitario: 0.001870 }
    ],
    tecido: [
      { produto_id: 'prod-tec-suede-bege', nome_exibicao: 'Faixa Lateral 514x35cm', quantidade: 1, unidade: 'un', m2_unitario: 1.799 }
    ],
    tnt: [
      { produto_id: 'prod-tnt-60-140', nome_exibicao: 'TNT 60" 140cm Tampo (÷2)', quantidade: 1.88, unidade: 'ml', m2_unitario: 1.316 },
      { produto_id: 'prod-tnt-40-140', nome_exibicao: 'TNT 40" 140cm Fundo (÷2)', quantidade: 1.88, unidade: 'ml', m2_unitario: 1.316 }
    ],
    ferragens: [
      { produto_id: 'prod-fer-grampo-1440', nome_exibicao: 'Grampo 14/40 Esqueleto', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-grampo-8008', nome_exibicao: 'Grampo 80/08 Tapeçaria', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-grampo-9220', nome_exibicao: 'Grampo 92/20 Chaparia', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-bucha', nome_exibicao: 'Bucha Americana Metal 2cm', quantidade: 6, unidade: 'un' },
      { produto_id: 'prod-fer-pe-12', nome_exibicao: 'Pé de Cama 12cm Plástico', quantidade: 6, unidade: 'un' },
      { produto_id: 'prod-fer-chapa-uniao', nome_exibicao: 'Chapa de União', quantidade: 1, unidade: 'un' }
    ],
    acabamento: [],
    embalagem: [
      { produto_id: 'prod-emb-manual', nome_exibicao: 'Manual de Instruções', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mal-600', nome_exibicao: 'Malha 600mm Revestimento', quantidade: 0.5, unidade: 'kg' },
      { produto_id: 'prod-emb-saco', nome_exibicao: 'Saco de Kit de Acessórios', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-emb-cantoneira-pap', nome_exibicao: 'Cantoneira de Papelão Proteção', quantidade: 4, unidade: 'un' }
    ]
  },
  {
    modelo: '0,69x1,88',
    tipo: 'BAÚ',
    custo_target: 159.84,
    madeira: [
      { produto_id: 'prod-mad-chapa-56', nome_exibicao: 'Chapa Compensado 175x56x0.5cm', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mad-chapa-69', nome_exibicao: 'Chapa Compensado 186x66x0.5cm', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Toco Pinus 25x5x2cm', quantidade: 20, unidade: 'un', volume_m3_unitario: 0.000250 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 58x5x2cm', quantidade: 4, unidade: 'un', volume_m3_unitario: 0.000580 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 63,5x5x2cm', quantidade: 6, unidade: 'un', volume_m3_unitario: 0.000635 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 68x5x2cm', quantidade: 3, unidade: 'un', volume_m3_unitario: 0.000680 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 100x7x2cm', quantidade: 2, unidade: 'un', volume_m3_unitario: 0.001400 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Ripa Pinus 187x5x2cm', quantidade: 2, unidade: 'un', volume_m3_unitario: 0.001870 },
      { produto_id: 'prod-mad-ripas', nome_exibicao: 'Trava Pinus', quantidade: 1, unidade: 'un', volume_m3_unitario: 0.002100 }
    ],
    tecido: [
      { produto_id: 'prod-tec-suede-bege', nome_exibicao: 'Faixa Base 514x38cm', quantidade: 1, unidade: 'un', m2_unitario: 1.9532 },
      { produto_id: 'prod-tec-suede-bege', nome_exibicao: 'Faixa Tampa 514x14cm', quantidade: 1, unidade: 'un', m2_unitario: 0.7196 }
    ],
    tnt: [
      { produto_id: 'prod-tnt-60-140', nome_exibicao: 'TNT 60" 140cm Tampo (÷2)', quantidade: 1.88, unidade: 'ml', m2_unitario: 1.316 },
      { produto_id: 'prod-tnt-60-140', nome_exibicao: 'TNT 60" 140cm Interna Baú (÷2)', quantidade: 1.88, unidade: 'ml', m2_unitario: 1.316 },
      { produto_id: 'prod-tnt-40-140', nome_exibicao: 'TNT 40" 140cm Interna Tampo (÷2)', quantidade: 1.88, unidade: 'ml', m2_unitario: 1.316 },
      { produto_id: 'prod-tnt-40-140', nome_exibicao: 'TNT 40" 140cm Fundo Baú (÷2)', quantidade: 1.88, unidade: 'ml', m2_unitario: 1.316 },
      { produto_id: 'prod-tnt-60-35', nome_exibicao: 'TNT 60" 35cm Faixa Interna', quantidade: 5.14, unidade: 'ml', m2_unitario: 1.799 }
    ],
    ferragens: [
      { produto_id: 'prod-fer-grampo-1440', nome_exibicao: 'Grampo 14/40 Esqueleto', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-grampo-8008', nome_exibicao: 'Grampo 80/08 Tapeçaria', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-grampo-9220', nome_exibicao: 'Grampo 92/20 Chaparia', quantidade: 300, unidade: 'un' },
      { produto_id: 'prod-fer-articulacao', nome_exibicao: 'Articulação (par) Baú', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-fer-pistao', nome_exibicao: 'Pistão amortecedor Baú', quantidade: 2, unidade: 'un' },
      { produto_id: 'prod-fer-parafuso-5030', nome_exibicao: 'Parafuso 5,0x30 Estrutural', quantidade: 16, unidade: 'un' },
      { produto_id: 'prod-fer-parafuso-3525', nome_exibicao: 'Parafuso 3,5x25 Chaparia', quantidade: 4, unidade: 'un' },
      { produto_id: 'prod-fer-arco', nome_exibicao: 'Arco de Suporte Colchão', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-fer-bucha', nome_exibicao: 'Bucha Americana Metal 2cm', quantidade: 6, unidade: 'un' },
      { produto_id: 'prod-fer-pe-6', nome_exibicao: 'Pé de Cama 6cm Plástico', quantidade: 6, unidade: 'un' },
      { produto_id: 'prod-fer-cantoneira-pl', nome_exibicao: 'Cantoneira Plástica Estética', quantidade: 4, unidade: 'un' },
      { produto_id: 'prod-fer-chapa-uniao', nome_exibicao: 'Chapa de União', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-fer-puxador', nome_exibicao: 'Puxador de Plástico Cama Baú', quantidade: 1, unidade: 'un' }
    ],
    acabamento: [
      { produto_id: 'prod-col-tambor', nome_exibicao: 'Cola Spray/Tambor', quantidade: 0.007, unidade: 'kg' },
      { produto_id: 'prod-acab-lixa', nome_exibicao: 'Lixa de Acabamento', quantidade: 0.1, unidade: 'un' }
    ],
    embalagem: [
      { produto_id: 'prod-emb-manual', nome_exibicao: 'Manual de Instruções', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-mal-600', nome_exibicao: 'Malha 600mm Revestimento', quantidade: 0.5, unidade: 'kg' },
      { produto_id: 'prod-emb-saco', nome_exibicao: 'Saco de Kit de Acessórios', quantidade: 1, unidade: 'un' },
      { produto_id: 'prod-emb-lateral-pap', nome_exibicao: 'Lateral de Papelão Protetora', quantidade: 2, unidade: 'un' }
    ]
  }
]

export function gerarCodigoLote(
  grupo: string,
  subgrupo: string
): string {
  const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '')
  const grpAbbr = grupo.substring(0, 3).toUpperCase()
  const subAbbr = subgrupo.substring(0, 4).toUpperCase()
  return `LOTE-${grpAbbr}-${subAbbr}-${dateStr}`
}

export function getLoteSpecsString(l?: Lote): string {
  if (!l) return ''
  const parts: string[] = []
  
  if (l.comprimento && l.largura && l.espessura) {
    parts.push(`${l.comprimento}x${l.largura}x${l.espessura}m`)
  } else if (l.largura) {
    parts.push(`Larg: ${l.largura}m`)
  }
  
  if (l.tamanho) parts.push(`Tam: ${l.tamanho}`)
  if (l.cor) parts.push(`Cor: ${l.cor}`)
  if (l.tipo_tear) parts.push(`Tear: ${l.tipo_tear}`)
  if (l.micras) parts.push(`${l.micras}µm`)
  if (l.gramatura) parts.push(`${l.gramatura}g`)
  if (l.pressao) parts.push(`P: ${l.pressao}`)
  if (l.lado_conjunto) parts.push(`Lado: ${l.lado_conjunto}`)
  if (l.peso_rolo) parts.push(`${l.peso_rolo}kg`)
  if (l.umidade) parts.push(`Umid: ${l.umidade}%`)
  if (l.tipo_cabeca) parts.push(`Cab: ${l.tipo_cabeca}`)
  if (l.marca) parts.push(`Marca: ${l.marca}`)
  if (l.condicao) parts.push(l.condicao.toUpperCase())
  
  return parts.join(' | ')
}

export function sanitizeInput(text: string): string {
  if (!text) return ''
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
}

